/**
 * Tipos del sistema de Roles y Permisos (RBAC) por empresa.
 *
 * Modelo granular basado en las tablas de Supabase:
 *   - company_users (empresa_id, user_id, role, status)
 *   - permissions   (role, module, can_view/create/edit/delete)
 *
 * El rol de un usuario es específico a cada empresa (relación N:M).
 * Los valores son string-literal unions para comparar directo con el backend.
 */

export type Role = 'owner' | 'admin' | 'manager' | 'operator' | 'viewer';

export const ROLES: Role[] = ['owner', 'admin', 'manager', 'operator', 'viewer'];

/** Roles con capacidades administrativas (gestión de usuarios, configuración). */
export const ADMIN_ROLES: Role[] = ['owner', 'admin'];

/**
 * Módulos de la aplicación. Cada uno se mapea a un conjunto de rutas/acciones.
 * Deben coincidir con los valores del CHECK de la tabla `permissions`.
 */
export type Module =
  | 'animals' // Gestión de animales (ganado, sesiones de carga)
  | 'locations' // Lotes, corrales, campos
  | 'financial' // Gastos, ingresos, libro diario, operaciones
  | 'reports' // Reportes y estadísticas
  | 'admin' // Gestión de usuarios, hoja de ruta, devtools
  | 'tools' // Herramientas varias (lluvias, notas, recordatorios)
  | 'animals_history' // Registros históricos de animales (auditoría / event-sourcing)
  | 'map' // Mapa del Campo (elementos_mapa): todos ven, escriben owner/admin/manager
  | 'sessions' // Sesiones de carga: unir sesiones (owner/admin/manager)
  | 'sigsa' // Declaración SIGSA (sigsa_declaraciones): todos ven, marcan owner/admin/manager
  | 'map_examples' // Ejemplos de ambientes ("Marcar ambientes"): todos ven, marcan owner/admin/manager/operator
  | 'puntos_control'; // Puntos de control (foto/observación de pastura): todos ven, cargan owner/admin/manager/operator, borran owner/admin/manager

export const MODULES: Module[] = [
  'animals',
  'locations',
  'financial',
  'reports',
  'admin',
  'tools',
  'animals_history',
  'map',
  'sessions',
  'sigsa',
  'map_examples',
  'puntos_control',
];

export type Action = 'view' | 'create' | 'edit' | 'delete';

export const ACTIONS: Action[] = ['view', 'create', 'edit', 'delete'];

export type UserStatus = 'pending' | 'active' | 'inactive' | 'suspended';

/** Membership de un usuario en una empresa (fila de `company_users`). */
export interface CompanyUser {
  id: string;
  empresa_id: string;
  user_id: string;
  role: Role;
  status: UserStatus;
  invited_by?: string | null;
  invited_at?: string;
  accepted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Membership enriquecida con datos de perfil (vista users_with_permissions). */
export interface CompanyUserWithProfile extends CompanyUser {
  email?: string;
  nombre?: string | null;
  apellido?: string | null;
}

/** Matriz de permisos por módulo para el usuario/empresa actual. */
export type UserPermissions = Partial<
  Record<Module, { canView: boolean; canCreate: boolean; canEdit: boolean; canDelete: boolean }>
>;

/** Empresa a la que pertenece el usuario (para el selector multi-empresa). */
export interface UserCompany {
  empresa_id: string;
  empresa_nombre: string;
  role: Role;
  status: UserStatus;
}

/** Invitación pendiente (fila de `invitations`). */
export interface Invitation {
  id: string;
  empresa_id: string;
  email: string;
  role: Role;
  invited_by: string;
  token: string;
  message?: string | null;
  expires_at: string;
  accepted_at?: string | null;
  created_at?: string;
}

/** Etiquetas legibles en español para roles (UI). */
export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Dueño',
  admin: 'Administrador',
  manager: 'Gerente',
  operator: 'Operador',
  viewer: 'Solo lectura',
};

/** Etiquetas legibles en español para módulos (UI). */
export const MODULE_LABELS: Record<Module, string> = {
  animals: 'Ganado',
  locations: 'Lotes y Campos',
  financial: 'Administración',
  reports: 'Reportes',
  admin: 'Administración del sistema',
  tools: 'Herramientas',
  animals_history: 'Historial de animales',
  map: 'Mapa del Campo',
  sessions: 'Sesiones de carga',
  sigsa: 'Declaración SIGSA',
  map_examples: 'Ejemplos de ambientes',
  puntos_control: 'Puntos de control',
};

/** Etiquetas legibles en español para estados de usuario. */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  pending: 'Pendiente',
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
};

/**
 * Matriz de permisos por rol y módulo.
 *
 * ⚠️ ESPEJO del seed de la tabla `permissions` de Supabase:
 *   `supabase/migrations/20260724030000_baseline_data_and_auth_trigger.sql` (líneas 35-76).
 * Debe mantenerse **sincronizada** con ese seed. Si cambiás los permisos en la
 * BD (nueva migración), actualizá también esta constante para que la UI
 * explicativa (gestión de usuarios) no muestre información desactualizada.
 *
 * Cada rol indica, por módulo, la lista de acciones permitidas
 * (view/create/edit/delete). Un módulo ausente del objeto equivale a "sin
 * acceso" al mismo (canView=false para todo).
 */
export const ROLE_PERMISSIONS: Record<Role, Partial<Record<Module, Action[]>>> = {
  // OWNER: acceso total.
  owner: {
    animals: ['view', 'create', 'edit', 'delete'],
    locations: ['view', 'create', 'edit', 'delete'],
    financial: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create', 'edit', 'delete'],
    admin: ['view', 'create', 'edit', 'delete'],
    tools: ['view', 'create', 'edit', 'delete'],
    animals_history: ['view', 'create', 'delete'],
    map: ['view', 'create', 'edit', 'delete'],
    sessions: ['view', 'edit'],
    sigsa: ['view', 'create', 'edit', 'delete'],
    map_examples: ['view', 'create', 'edit', 'delete'],
    puntos_control: ['view', 'create', 'edit', 'delete'],
  },
  // ADMIN: casi todo; en el módulo admin solo puede ver y crear (no editar/eliminar owners).
  admin: {
    animals: ['view', 'create', 'edit', 'delete'],
    locations: ['view', 'create', 'edit', 'delete'],
    financial: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create', 'edit', 'delete'],
    admin: ['view', 'create'],
    tools: ['view', 'create', 'edit', 'delete'],
    animals_history: ['view', 'create', 'delete'],
    map: ['view', 'create', 'edit', 'delete'],
    sessions: ['view', 'edit'],
    sigsa: ['view', 'create', 'edit', 'delete'],
    map_examples: ['view', 'create', 'edit', 'delete'],
    puntos_control: ['view', 'create', 'edit', 'delete'],
  },
  // MANAGER: operacional y reportes; sin gestión de usuarios.
  manager: {
    animals: ['view', 'create', 'edit'],
    locations: ['view', 'create', 'edit'],
    financial: ['view', 'create'],
    reports: ['view'],
    tools: ['view', 'create', 'edit', 'delete'],
    animals_history: ['view', 'create'],
    map: ['view', 'create', 'edit', 'delete'],
    sessions: ['view', 'edit'],
    sigsa: ['view', 'create', 'edit', 'delete'],
    map_examples: ['view', 'create', 'edit', 'delete'],
    puntos_control: ['view', 'create', 'edit', 'delete'],
  },
  // OPERATOR: operaciones básicas; sin acceso a Administración.
  // animals_history: view (ve histórico/sesiones) + create (edita/crea animales),
  // pero SIN delete (no borra animales ni anula/edita registros ni revierte sesiones).
  // map: solo ve el mapa (el dibujo es de owner/admin/manager).
  // map_examples: marca ejemplos de ambiente (recorre el campo; decisión 2026-10-05).
  // puntos_control: crea puntos/observaciones/fotos y edita (la subida offline
  // de fotos finaliza con un UPDATE), pero no borra (decisión 2026-10-12).
  operator: {
    animals: ['view', 'create', 'edit'],
    locations: ['view'],
    reports: ['view'],
    tools: ['view', 'create', 'edit', 'delete'],
    animals_history: ['view', 'create'],
    map: ['view'],
    sessions: ['view'],
    sigsa: ['view'],
    map_examples: ['view', 'create', 'edit', 'delete'],
    puntos_control: ['view', 'create', 'edit'],
  },
  // VIEWER: solo lectura.
  viewer: {
    animals: ['view'],
    locations: ['view'],
    financial: ['view'],
    reports: ['view'],
    tools: ['view'],
    animals_history: ['view'],
    map: ['view'],
    sessions: ['view'],
    sigsa: ['view'],
    map_examples: ['view'],
    puntos_control: ['view'],
  },
};

/**
 * Descripción breve, en lenguaje llano, de cada rol.
 * Pensada para mostrarla junto al selector de rol al invitar/asignar usuarios.
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Acceso total a la empresa, incluida la gestión de usuarios y la configuración.',
  admin: 'Casi todo lo del Dueño, pero no puede editar ni eliminar otros Dueños.',
  manager: 'Carga y edición operativa (ganado, lotes) y consulta de reportes. Sin gestión de usuarios.',
  operator: 'Operación diaria con ganado y herramientas. Sin acceso a Administración.',
  viewer: 'Solo consulta: ver datos sin modificar nada.',
};

/**
 * Verifica si un rol tiene una acción sobre un módulo, consultando `ROLE_PERMISSIONS`.
 * Útil para UI explicativa y para mostrar matrices por rol sin duplicar la lógica.
 */
export function roleHasPermission(role: Role, module: Module, action: Action): boolean {
  const actions = ROLE_PERMISSIONS[role]?.[module];
  return Array.isArray(actions) && actions.includes(action);
}
